/**
 * @file Chapter list, create, edit, delete — sort order column, drag-and-drop reorder, RBAC.
 */
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DragHandleIcon from "@mui/icons-material/DragHandle";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { canManageContent, isAdmin } from "@/lib/permissions";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";
import { slugify } from "@/lib/shared";

/** URL param and select value for listing every chapter (reorder disabled). */
const ALL_CHAPTERS = "all";

interface GuideOption {
  id: string;
  title: string;
  slug: string;
}

interface ChapterRow {
  id: string;
  guideId: string;
  title: string;
  slug: string;
  slugAutoFromTitle: boolean;
  sortOrder: number;
  guide: { id: string; title: string; slug: string };
  _count: { pages: number };
}

function SortableChapterRow({
  chapter,
  onEdit,
  onRemove,
  canDelete,
}: {
  chapter: ChapterRow;
  onEdit: (c: ChapterRow) => void;
  onRemove: (c: ChapterRow) => void;
  canDelete: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: chapter.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.55 : undefined,
  };

  return (
    <TableRow ref={setNodeRef} style={style} hover>
      <TableCell
        sx={{
          width: 48,
          pr: 0,
          verticalAlign: "middle",
        }}
      >
        <Tooltip title="Drag to reorder">
          <IconButton
            size="small"
            {...attributes}
            {...listeners}
            sx={{ cursor: "grab", touchAction: "none" }}
            aria-label="Drag to reorder chapter"
          >
            <DragHandleIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </TableCell>
      <TableCell sx={{ fontWeight: 600 }}>
        <Link
          to={`/dashboard/pages?chapterId=${chapter.id}`}
          style={{ color: "inherit", textDecoration: "none" }}
        >
          {chapter.title}
        </Link>
      </TableCell>
      <TableCell sx={{ color: "text.secondary" }}>{chapter.slug}</TableCell>
      <TableCell>{chapter.sortOrder + 1}</TableCell>
      <TableCell>{chapter._count.pages}</TableCell>
      <TableCell align="right">
        <Tooltip title="Edit chapter">
          <IconButton size="small" color="primary" onClick={() => onEdit(chapter)} aria-label="Edit chapter">
            <EditOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {canDelete ? (
          <Tooltip title="Delete chapter">
            <IconButton size="small" color="error" onClick={() => void onRemove(chapter)} aria-label="Delete chapter">
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null}
      </TableCell>
    </TableRow>
  );
}

function StaticChapterRow({
  chapter,
  onEdit,
  onRemove,
  canDelete,
  showGuide,
}: {
  chapter: ChapterRow;
  onEdit: (c: ChapterRow) => void;
  onRemove: (c: ChapterRow) => void;
  canDelete: boolean;
  showGuide: boolean;
}) {
  return (
    <TableRow hover>
      {showGuide ? (
        <TableCell sx={{ color: "text.secondary", maxWidth: 220 }}>{chapter.guide.title}</TableCell>
      ) : null}
      <TableCell sx={{ fontWeight: 600 }}>
        <Link
          to={`/dashboard/pages?chapterId=${chapter.id}`}
          style={{ color: "inherit", textDecoration: "none" }}
        >
          {chapter.title}
        </Link>
      </TableCell>
      <TableCell sx={{ color: "text.secondary" }}>{chapter.slug}</TableCell>
      <TableCell>{chapter.sortOrder + 1}</TableCell>
      <TableCell>{chapter._count.pages}</TableCell>
      <TableCell align="right">
        <Tooltip title="Edit chapter">
          <IconButton size="small" color="primary" onClick={() => onEdit(chapter)} aria-label="Edit chapter">
            <EditOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {canDelete ? (
          <Tooltip title="Delete chapter">
            <IconButton size="small" color="error" onClick={() => void onRemove(chapter)} aria-label="Delete chapter">
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null}
      </TableCell>
    </TableRow>
  );
}

export function ChaptersPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const canEdit = useMemo(() => canManageContent(user?.roles), [user?.roles]);
  const canDelete = useMemo(() => isAdmin(user?.roles), [user?.roles]);

  const [guides, setGuides] = useState<GuideOption[]>([]);
  const [guideId, setGuideId] = useState<string>("");
  const [rows, setRows] = useState<ChapterRow[]>([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [createGuideId, setCreateGuideId] = useState("");
  const [createTitle, setCreateTitle] = useState("");
  const [createSlug, setCreateSlug] = useState("");
  const [createSlugManual, setCreateSlugManual] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<ChapterRow | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const [editSlug, setEditSlug] = useState("");
  const [editSlugManual, setEditSlugManual] = useState(false);
  const [reorderPending, setReorderPending] = useState(false);
  const [chapterToDelete, setChapterToDelete] = useState<ChapterRow | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    void apiFetch<GuideOption[]>("/api/v1/guides")
      .then(setGuides)
      .catch((e) => setError(String(e)));
  }, []);

  useEffect(() => {
    if (guides.length === 0) {
      return;
    }
    const fromUrl = searchParams.get("guideId");
    let pick: string;
    if (fromUrl === ALL_CHAPTERS) {
      pick = ALL_CHAPTERS;
    } else if (fromUrl && guides.some((g) => g.id === fromUrl)) {
      pick = fromUrl;
    } else {
      pick = guides[0].id;
    }
    setGuideId(pick);
    if (pick !== fromUrl) {
      navigate(
        pick === ALL_CHAPTERS ? `/dashboard/chapters?guideId=${ALL_CHAPTERS}` : `/dashboard/chapters?guideId=${pick}`,
        { replace: true },
      );
    }
  }, [guides, searchParams, navigate]);

  const load = useCallback(async () => {
    if (!guideId) {
      setRows([]);
      return;
    }
    if (guideId === ALL_CHAPTERS) {
      const data = await apiFetch<ChapterRow[]>("/api/v1/chapters");
      setRows(data);
      return;
    }
    const data = await apiFetch<ChapterRow[]>(`/api/v1/chapters?guideId=${guideId}`);
    setRows(data);
  }, [guideId]);

  useEffect(() => {
    void load().catch((e) => setError(String(e)));
  }, [load]);

  function openCreateDialog() {
    setCreateTitle("");
    setCreateSlug("");
    setCreateSlugManual(false);
    setCreateGuideId(guideId && guideId !== ALL_CHAPTERS ? guideId : guides[0]?.id || "");
    setCreateOpen(true);
  }

  async function createChapter(e: React.FormEvent) {
    e.preventDefault();
    const gid = createGuideId;
    if (!gid) {
      setError("Select a guide first.");
      return;
    }
    setError(null);
    await apiFetch("/api/v1/chapters", {
      method: "POST",
      json: {
        guideId: gid,
        title: createTitle,
        ...(createSlugManual ? { slug: createSlug } : {}),
      },
    });
    setCreateOpen(false);
    setCreateTitle("");
    setCreateSlug("");
    setCreateSlugManual(false);
    if (gid !== guideId && guideId !== ALL_CHAPTERS) {
      setGuideId(gid);
      navigate(`/dashboard/chapters?guideId=${gid}`, { replace: true });
    }
    await load();
  }

  function openEdit(c: ChapterRow) {
    setEditing(c);
    setEditTitle(c.title);
    setEditSlug(c.slug);
    setEditSlugManual(!c.slugAutoFromTitle);
  }

  async function saveEdit(e: React.FormEvent) {
    e.preventDefault();
    if (!editing) {
      return;
    }
    setError(null);
    await apiFetch(`/api/v1/chapters/${editing.id}`, {
      method: "PATCH",
      json: { title: editTitle, slug: editSlug },
    });
    setEditing(null);
    await load();
  }

  function requestDeleteChapter(c: ChapterRow) {
    setChapterToDelete(c);
  }

  async function confirmDeleteChapter() {
    if (!chapterToDelete) {
      return;
    }
    const c = chapterToDelete;
    setChapterToDelete(null);
    setError(null);
    try {
      await apiFetch(`/api/v1/chapters/${c.id}`, { method: "DELETE" });
      await load();
    } catch (e) {
      setError(String(e));
    }
  }

  const deleteChapterMessage = chapterToDelete
    ? chapterToDelete._count.pages > 0
      ? `This chapter has ${chapterToDelete._count.pages} page(s). Deleting it will also remove those pages from the CMS and help site. Continue?`
      : `Delete chapter “${chapterToDelete.title}”?`
    : "";

  async function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!guideId || guideId === ALL_CHAPTERS) {
      return;
    }
    if (!over || active.id === over.id) {
      return;
    }
    const oldIndex = rows.findIndex((r) => r.id === active.id);
    const newIndex = rows.findIndex((r) => r.id === over.id);
    if (oldIndex < 0 || newIndex < 0) {
      return;
    }
    const reordered = arrayMove(rows, oldIndex, newIndex);
    setRows(reordered);
    setReorderPending(true);
    setError(null);
    try {
      const updated = await apiFetch<ChapterRow[]>("/api/v1/chapters/reorder", {
        method: "POST",
        json: { guideId, chapterIds: reordered.map((r) => r.id) },
      });
      setRows(updated);
    } catch (e) {
      setError(String(e));
      await load();
    } finally {
      setReorderPending(false);
    }
  }

  const sortableIds = rows.map((r) => r.id);
  const showGuideColumn = guideId === ALL_CHAPTERS;
  const reorderEnabled = Boolean(canEdit && guideId && guideId !== ALL_CHAPTERS);

  const tableContent = (
    <Table size="medium">
      <TableHead>
        <TableRow sx={{ bgcolor: "action.hover" }}>
          {reorderEnabled ? <TableCell width={48} aria-label="Reorder" /> : null}
          {showGuideColumn ? <TableCell>Guide</TableCell> : null}
          <TableCell>Title</TableCell>
          <TableCell>Slug</TableCell>
          <TableCell width={100}>Sort order</TableCell>
          <TableCell>Pages</TableCell>
          {canEdit ? (
            <TableCell align="right" width={120}>
              Actions
            </TableCell>
          ) : null}
        </TableRow>
      </TableHead>
      <TableBody>
        {reorderEnabled ? (
          <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
            {rows.map((c) => (
              <SortableChapterRow
                key={c.id}
                chapter={c}
                onEdit={openEdit}
                onRemove={requestDeleteChapter}
                canDelete={canDelete}
              />
            ))}
          </SortableContext>
        ) : canEdit ? (
          rows.map((c) => (
            <StaticChapterRow
              key={c.id}
              chapter={c}
              onEdit={openEdit}
              onRemove={requestDeleteChapter}
              canDelete={canDelete}
              showGuide={showGuideColumn}
            />
          ))
        ) : (
          rows.map((c) => (
            <TableRow key={c.id} hover>
              {showGuideColumn ? (
                <TableCell sx={{ color: "text.secondary", maxWidth: 220 }}>{c.guide.title}</TableCell>
              ) : null}
              <TableCell sx={{ fontWeight: 600 }}>
                <Link to={`/dashboard/pages?chapterId=${c.id}`} style={{ color: "inherit", textDecoration: "none" }}>
                  {c.title}
                </Link>
              </TableCell>
              <TableCell sx={{ color: "text.secondary" }}>{c.slug}</TableCell>
              <TableCell>{c.sortOrder + 1}</TableCell>
              <TableCell>{c._count.pages}</TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
          mb: 2,
        }}
      >
        <Typography variant="h4" component="h1" fontWeight={700}>
          Chapters
        </Typography>
        {canEdit && guides.length > 0 ? (
          <Button variant="contained" onClick={openCreateDialog}>
            Add chapter
          </Button>
        ) : null}
      </Box>
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      {guides.length > 0 ? (
        <Paper
          sx={{
            p: 2,
            mb: 3,
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 2,
            border: 1,
            borderColor: "divider",
          }}
        >
          <FormControl size="small" sx={{ minWidth: 220, flex: "1 1 200px" }}>
            <InputLabel id="chapters-guide-label">Guide</InputLabel>
            <Select
              labelId="chapters-guide-label"
              label="Guide"
              value={guideId}
              onChange={(e) => {
                const v = e.target.value as string;
                setGuideId(v);
                navigate(
                  v === ALL_CHAPTERS ? `/dashboard/chapters?guideId=${ALL_CHAPTERS}` : `/dashboard/chapters?guideId=${v}`,
                  { replace: true },
                );
              }}
            >
              <MenuItem value={ALL_CHAPTERS}>All chapters</MenuItem>
              {guides.map((g) => (
                <MenuItem key={g.id} value={g.id}>
                  {g.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="body2" color="text.secondary" sx={{ flex: "1 1 180px", maxWidth: 360 }}>
            Manage guides in{" "}
            <Link to="/dashboard/guides" style={{ color: "inherit", fontWeight: 600 }}>
              Guides
            </Link>
            .
          </Typography>
        </Paper>
      ) : null}

      {reorderEnabled ? (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => void handleDragEnd(e)}>
          <TableContainer
            component={Paper}
            sx={{
              border: 1,
              borderColor: "divider",
              opacity: reorderPending ? 0.7 : 1,
              transition: "opacity 0.2s",
            }}
          >
            {tableContent}
          </TableContainer>
        </DndContext>
      ) : (
        <TableContainer component={Paper} sx={{ border: 1, borderColor: "divider" }}>
          {tableContent}
        </TableContainer>
      )}

      <ConfirmDialog
        open={chapterToDelete !== null}
        title="Delete chapter?"
        message={deleteChapterMessage}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        danger
        onConfirm={() => void confirmDeleteChapter()}
        onClose={() => setChapterToDelete(null)}
      />

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add chapter</DialogTitle>
        <Box component="form" onSubmit={(e) => void createChapter(e)}>
          <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <FormControl fullWidth required>
              <InputLabel id="create-chapter-guide-label">Guide</InputLabel>
              <Select
                labelId="create-chapter-guide-label"
                label="Guide"
                value={createGuideId}
                onChange={(e) => setCreateGuideId(e.target.value as string)}
              >
                {guides.map((g) => (
                  <MenuItem key={g.id} value={g.id}>
                    {g.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Title"
              fullWidth
              required
              autoFocus
              value={createTitle}
              onChange={(e) => {
                const v = e.target.value;
                setCreateTitle(v);
                if (!createSlugManual) {
                  setCreateSlug(slugify(v));
                }
              }}
            />
            <TextField
              label="Slug"
              fullWidth
              required
              value={createSlug}
              onChange={(e) => {
                setCreateSlugManual(true);
                setCreateSlug(e.target.value);
              }}
              helperText="Filled from title until you edit it."
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button type="submit" variant="contained">
              Add chapter
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog open={Boolean(editing)} onClose={() => setEditing(null)} fullWidth maxWidth="sm">
        <DialogTitle>Edit chapter</DialogTitle>
        <Box component="form" onSubmit={saveEdit}>
          <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <TextField
              label="Title"
              fullWidth
              required
              value={editTitle}
              onChange={(e) => {
                const v = e.target.value;
                setEditTitle(v);
                if (!editSlugManual) {
                  setEditSlug(slugify(v));
                }
              }}
            />
            <TextField
              label="Slug"
              fullWidth
              required
              value={editSlug}
              onChange={(e) => {
                setEditSlugManual(true);
                setEditSlug(e.target.value);
              }}
              helperText="Stays in sync with title until you edit the slug."
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setEditing(null)}>Cancel</Button>
            <Button type="submit" variant="contained">
              Save
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </Box>
  );
}
